import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Text } from '@/nova/components';
import { PLAYABLE_PICKER } from '@/nova/theme/tokens';

import { EMPTY_DEMO_STACK, demoLayerShown, demoSettleDelay, demoStackReducer } from '../../card-demo-layers';
import type { PlayableTemplate } from '../../playable-api';
import { previewControlFor, type CardDemoIntent } from '../../playable-preview';
import { PreviewFrame, fitScale, formatFrameSize, type PreviewFrameHandle } from './PreviewFrame';
import * as S from './PlayableComposer.styles';

/** A preview being fetched or shown: loading, the document, or why there is none. */
export interface PreviewState {
  status: 'loading' | 'ready' | 'unavailable' | 'error';
  /** The document a picker card shows, for its whole life: mounted as a controlled preview (`preview: true`), it
   *  rests and plays through as the card asks. */
  html?: string;
  /** A runtime without the control channel only: the playthrough a hovered card loads over its loop; absent = the
   *  card replays `html`. */
  playHtml?: string;
}

export interface TemplatePickerProps {
  /** The templates that can advertise this mini-game's subject, in the registry's order. */
  templates: PlayableTemplate[];
  /** The chosen template ('' when none yet). */
  selectedId: string;
  /** Each card's live demo, by template id (absent = not requested yet). */
  previews: Record<string, PreviewState>;
  /** The environment can render previews (the HTML5 runtime is built). */
  canPreview: boolean;
  /** The format the cards preview in. */
  previewFormat: string;
  disabled?: boolean;
  /** The card preview box (default 160 x 240, a portrait game); an HTML5 banner style previews in a wider box. */
  cardW?: number;
  cardH?: number;
  /** A short list shares the whole row (the cards stretch) instead of leaving room for cards that are not there. */
  fit?: boolean;
  /** Translated accessible name of the card group (default: "Game"). */
  ariaLabel?: string;
  onPick: (templateId: string) => void;
}

/** The default card preview box (the demo is laid out at its real size and shrunk into it). */
const CARD_W = 160;
const CARD_H = 240;

/** Spreads the resting cards' loop restarts so the grid never restarts all at once. Pure. */
const restPhaseMs = (index: number): number => (index * 2777) % PLAYABLE_PICKER.IDLE_REMOUNT_MS;

/** Whether the card's runtime takes `spk-preview-control` messages: not known yet, yes (its document reported a
 *  state), or no (none reported within `CONTROL_WAIT_MS` of loading: a runtime that predates the channel, or a
 *  document the backend built). */
type DemoChannel = 'unknown' | 'controlled' | 'legacy';

interface CardDemoProps {
  state: PreviewState & { html: string };
  playing: boolean;
  run: number;
  index: number;
  w: number;
  h: number;
  scale: number;
  title: string;
}

/**
 * One card's demo, in ONE document for the card's life: it rests (a game's attract loop, a banner's final frame),
 * plays through when the card is hovered or focused, rests again (cross-faded inside the document) when the pointer
 * leaves, and replays on Replay, all by `postMessage` to the controlled preview (no remount, no reload). An edited
 * document cross-fades in over the old one.
 *
 * A runtime without the control channel gets the older demo: the playthrough loads hidden over the loop and fades
 * in, fading back out on leave over the loop still running underneath, and the loop restarts on a timer (each card
 * on its own phase) before the runtime's 30 s ambient-motion stop freezes it.
 */
const CardDemo = ({ state, playing, run, index, w, h, scale, title }: CardDemoProps): React.ReactElement => {
  const frameRef = React.useRef<PreviewFrameHandle>(null);
  const [channel, setChannel] = React.useState<DemoChannel>('unknown');
  const [loads, setLoads] = React.useState(0);
  const live = React.useRef({ channel, playing });
  live.current = { channel, playing };

  // The first state a document reports proves the channel. A document mounted afresh (an edit) starts resting, so
  // a still-hovered card plays it at once.
  const onPreviewState = React.useCallback((_state: unknown, fresh: boolean): void => {
    if (fresh && live.current.channel === 'controlled' && live.current.playing) frameRef.current?.post('play');
    setChannel('controlled');
  }, []);

  // Syncs with a clock: no state within the wait after the document loaded = a runtime without the channel.
  React.useEffect(() => {
    if (channel !== 'unknown' || loads === 0) return undefined;
    const timer = window.setTimeout(
      () => setChannel((cur) => (cur === 'unknown' ? 'legacy' : cur)),
      PLAYABLE_PICKER.CONTROL_WAIT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [channel, loads]);

  // Controlled: each change of hover or Replay sends its one control (none until the channel is proven, then the
  // pending one, so a hover during the wait still plays).
  const sent = React.useRef<CardDemoIntent>({ playing: false, run });
  React.useEffect(() => {
    if (channel !== 'controlled') return;
    const next: CardDemoIntent = { playing, run };
    const action = previewControlFor(sent.current, next);
    sent.current = next;
    if (action) frameRef.current?.post(action);
  }, [channel, playing, run]);

  /* ---- a runtime without the control channel: the two-document demo ---- */
  const legacy = channel === 'legacy';
  const [stack, dispatch] = React.useReducer(demoStackReducer, EMPTY_DEMO_STACK);
  const [restCycle, setRestCycle] = React.useState(0);
  const latestPlay = React.useRef(state.playHtml ?? state.html);
  latestPlay.current = state.playHtml ?? state.html;

  // Syncs with a clock: restarts the resting loop before the runtime's ambient-motion stop freezes it.
  React.useEffect(() => {
    if (!legacy) return undefined;
    let timer = window.setTimeout(function tick() {
      setRestCycle((n) => n + 1);
      timer = window.setTimeout(tick, PLAYABLE_PICKER.IDLE_REMOUNT_MS);
    }, PLAYABLE_PICKER.IDLE_REMOUNT_MS - restPhaseMs(index));
    return () => window.clearTimeout(timer);
  }, [index, legacy]);

  // The playthrough starts on hover (again on Replay) and fades out on leave.
  React.useEffect(() => {
    if (legacy && playing) dispatch({ type: 'mount', mode: 'play', html: latestPlay.current });
    else dispatch({ type: 'stop', at: Date.now() });
  }, [legacy, playing, run]);

  // Drops each faded-out playthrough once its fade has finished.
  React.useEffect(() => {
    const now = Date.now();
    const delay = demoSettleDelay(stack, now, PLAYABLE_PICKER.CROSSFADE_MS);
    if (delay === null) return undefined;
    const due = now + delay;
    const timer = window.setTimeout(
      () => dispatch({ type: 'settle', at: Math.max(Date.now(), due), fadeMs: PLAYABLE_PICKER.CROSSFADE_MS }),
      delay,
    );
    return () => window.clearTimeout(timer);
  }, [stack]);

  return (
    <S.DemoStackBox $w={w * scale} $h={h * scale}>
      <S.DemoLayer $shown $top={false}>
        <PreviewFrame
          ref={frameRef}
          html={state.html}
          run={restCycle}
          w={w}
          h={h}
          scale={scale}
          title={title}
          onLoad={() => setLoads((n) => n + 1)}
          onPreviewState={onPreviewState}
        />
      </S.DemoLayer>
      {stack.layers.map((layer) => (
        <S.DemoLayer key={layer.key} $shown={demoLayerShown(layer)} $top>
          <PreviewFrame
            html={layer.html}
            w={w}
            h={h}
            scale={scale}
            title={title}
            onLoad={() => dispatch({ type: 'loaded', key: layer.key, at: Date.now() })}
          />
        </S.DemoLayer>
      ))}
    </S.DemoStackBox>
  );
};

/**
 * The game picker of a new mini-game (and the style picker of a new HTML5 banner): one card per template, each
 * resting on its demo's resting loop and playing it through when hovered or focused (Replay runs it again), in a
 * sandboxed frame with the mini-game's copy and colours. Every game ends on the same offer; the pick only changes
 * the mechanic.
 */
export const TemplatePicker = ({
  templates,
  selectedId,
  previews,
  canPreview,
  previewFormat,
  disabled = false,
  cardW = CARD_W,
  cardH = CARD_H,
  fit = false,
  ariaLabel,
  onPick,
}: TemplatePickerProps): React.ReactElement => {
  const { t } = useTranslation();
  const { w, h } = formatFrameSize(previewFormat);
  const scale = fitScale(w, h, cardW, cardH);
  const [active, setActive] = React.useState<string | null>(null);
  const [runs, setRuns] = React.useState<Record<string, number>>({});
  const leave = (id: string): void => setActive((cur) => (cur === id ? null : cur));
  return (
    <S.TemplateGrid role="radiogroup" aria-label={ariaLabel ?? t('Game')} $minCard={cardW + 40} $fit={fit}>
      {templates.map((tpl, index) => {
        const selected = tpl.id === selectedId;
        const preview = previews[tpl.id];
        const ready = canPreview && preview?.status === 'ready' && !!preview.html;
        const playing = active === tpl.id;
        return (
          <S.TemplateCardWrap
            key={tpl.id}
            onMouseEnter={() => setActive(tpl.id)}
            onMouseLeave={() => leave(tpl.id)}
            onFocus={() => setActive(tpl.id)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) leave(tpl.id);
            }}
          >
            <S.TemplateCard
              type="button"
              role="radio"
              aria-checked={selected}
              $selected={selected}
              disabled={disabled}
              onClick={() => {
                if (!disabled) onPick(tpl.id);
              }}
            >
              <S.TemplatePreviewSlot $w={cardW} $h={cardH}>
                {!canPreview ? (
                  <S.NoteText text="Live previews are not available in this environment yet." />
                ) : ready && preview?.html ? (
                  <CardDemo
                    state={{ ...preview, html: preview.html }}
                    playing={playing}
                    run={runs[tpl.id] ?? 0}
                    index={index}
                    w={w}
                    h={h}
                    scale={scale}
                    title={t('{{name}} preview', { name: tpl.label })}
                  />
                ) : preview?.status === 'unavailable' ? (
                  <S.NoteText text="Live previews are not available in this environment yet." />
                ) : preview?.status === 'error' ? (
                  <S.NoteText text="The preview couldn't be loaded." />
                ) : (
                  <S.Spinner aria-hidden="true" />
                )}
              </S.TemplatePreviewSlot>
              <S.TemplateName raw text={tpl.label} />
              {tpl.schema.description ? <S.TemplateText raw text={tpl.schema.description} /> : null}
              {selected ? <S.SelectedMark text="Selected" /> : null}
            </S.TemplateCard>
            {ready ? (
              <S.CardReplay
                type="button"
                aria-label={t('Replay')}
                onClick={() => {
                  setActive(tpl.id);
                  setRuns((cur) => ({ ...cur, [tpl.id]: (cur[tpl.id] ?? 0) + 1 }));
                }}
              >
                <Icon name="refresh" size={12} />
                <Text text="Replay" />
              </S.CardReplay>
            ) : null}
          </S.TemplateCardWrap>
        );
      })}
    </S.TemplateGrid>
  );
};

export default TemplatePicker;
